// Tốc độ hấp thụ linh khí (CULTIVATION §4, BALANCE §2.2):
//   qiRate/s = QI_RATE_BASE × 6^r × N × D × C × T × B × (1 + H)
// Mỗi hệ số là một hàm thuần để test khớp bảng BALANCE §2.5.
import { BALANCE } from '@data/balance';
import { siteDef, zoneVeinMult, type CultivationSite } from '@data/content/sites';
import { techniqueDef } from '@data/content/techniques';
import { isZoneId, zoneDef } from '@data/content/zones';
import { stepInfo } from './steps';
import type { CultivationState } from './types';

export interface RateFactors {
  realmIdx: number;
  /** Nồng độ linh khí nơi bế quan. */
  N: number;
  /** Đăng Căn. */
  D: number;
  /** Công pháp chủ tu. */
  C: number;
  /** Tụ Linh Trận. */
  T: number;
  /** Đan / buff. */
  B: number;
  /** Đăng Hỏa (P2; P0 = 0). */
  H: number;
  /** Trang bị: 1 + Hấp Linh %. */
  E: number;
}

export function qiRateOf(f: RateFactors): number {
  return (
    BALANCE.QI_RATE_BASE *
    Math.pow(BALANCE.QI_RATE_REALM_GROWTH, f.realmIdx) *
    f.N *
    f.D *
    f.C *
    f.T *
    f.B *
    (1 + f.H) *
    f.E
  );
}

/** N = B_vùng × phẩm × M × (khe ngoài ? 0,8 : 1) × (nơi thấp hơn cảnh giới ≥ 2 bậc ? 0,5 : 1). */
export function siteDensity(site: CultivationSite, step: number): number {
  const r = stepInfo(step).realmIdx;
  const base = BALANCE.ZONE_BASE_DENSITY[site.realmTier] ?? 1;
  return (
    base *
    BALANCE.VEIN_GRADE_MULT[site.veinGrade] *
    zoneVeinMult(site.zoneId, step) *
    (site.outerSeat ? BALANCE.OUTER_SEAT_MULT : 1) *
    (site.realmTier <= r - BALANCE.SITE_TIER_PENALTY_GAP ? BALANCE.SITE_TIER_PENALTY_MULT : 1)
  );
}

/** Đăng Căn: 1 + 0,1 × r (+ cơ duyên, Đăng Hỏa ở giai đoạn sau), sàn 0,8. */
export function lampRoot(realmIdx: number): number {
  return Math.max(BALANCE.LAMP_ROOT_MIN, BALANCE.LAMP_ROOT_BASE + BALANCE.LAMP_ROOT_PER_REALM * realmIdx);
}

/** Hệ số công pháp: phẩm × (1 + 0,08 × (tầng − 1)) × (trùng hệ nơi bế quan ? 1,1 : 1). */
export function techniqueMult(techniqueId: string, layer: number, site: CultivationSite): number {
  const t = techniqueDef(techniqueId);
  const match = t.element !== null && t.element === site.element;
  return (
    BALANCE.TECHNIQUE_GRADE_MULT[t.grade] *
    (1 + BALANCE.TECHNIQUE_LAYER_BONUS * (layer - 1)) *
    (match ? 1 + BALANCE.TECHNIQUE_ELEMENT_MATCH_BONUS : 1)
  );
}

/** Tụ Linh Trận chỉ có tác dụng tại động phủ của người chơi. */
export function gatherArrayMult(site: CultivationSite, lv: number): number {
  return site.kind === 'cave' ? 1 + BALANCE.GATHER_ARRAY_QI_PER_LV * lv : 1;
}

/** Bể buff còn hạn ở `atMs`, trần BUFF_BUCKET_CAP. */
export function buffMult(state: CultivationState, atMs: number): number {
  let sum = 0;
  for (const b of state.buffs) if (b.untilMs > atMs) sum += b.bucket;
  return 1 + Math.min(sum, BALANCE.BUFF_BUCKET_CAP);
}

/** Nơi đang hút khí: Lịch Luyện thì theo nơi của vùng (Đăng Căn vẫn hút khí dọc đường). */
export function activeSiteId(state: CultivationState): string {
  if (state.idle.mode === 'trial' && state.idle.trialZoneId && isZoneId(state.idle.trialZoneId))
    return zoneDef(state.idle.trialZoneId).siteId;
  return state.idle.locationId;
}

export function rateFactors(state: CultivationState, atMs: number): RateFactors {
  const site = siteDef(activeSiteId(state));
  const realmIdx = stepInfo(state.step).realmIdx;
  return {
    realmIdx,
    N: siteDensity(site, state.step),
    D: lampRoot(realmIdx),
    C: techniqueMult(state.technique.mainId, state.technique.layer, site),
    T: gatherArrayMult(site, state.gatherArrayLv),
    B: buffMult(state, atMs),
    H: 0,
    E: 1 + Math.max(0, state.gear.qiRatePct) / 100,
  };
}

/** Linh khí / giây khi bế quan online 100%, chưa nhân Tử Quan. */
export function qiRate(state: CultivationState, atMs: number): number {
  return qiRateOf(rateFactors(state, atMs));
}

/** Hiệu suất: online 1; offline min(0,85, 0,60 + 0,025 × Tụ Linh) chỉ tại động phủ. */
export function efficiency(state: CultivationState, online: boolean): number {
  if (online) return BALANCE.ONLINE_EFF;
  const site = siteDef(activeSiteId(state));
  const arr = site.kind === 'cave' ? BALANCE.OFFLINE_EFF_PER_ARRAY_LV * state.gatherArrayLv : 0;
  // Bế Quan Hiệu Suất từ đồ: tổng ≤ +5 điểm (ITEMS §4.3 #41), không vượt trần 85%.
  const gear = Math.min(BALANCE.GEAR_OFFLINE_EFF_CAP_PTS, Math.max(0, state.gear.offlineEffPct)) / 100;
  return Math.min(BALANCE.OFFLINE_EFF_CAP, BALANCE.OFFLINE_EFF_BASE + arr + gear);
}

/** Hệ số nhân Tâm Đắc từ đặc hiệu tầng của công pháp chủ tu. */
export function masteryMult(state: CultivationState): number {
  const t = techniqueDef(state.technique.mainId);
  let k = 1;
  for (const p of t.layerPerks)
    if (state.technique.layer >= p.layer && p.effect.kind === 'masteryMult') k += p.effect.value;
  return k;
}
